import { gql } from "graphql-tag";
import { useMessage } from "naive-ui";
import { useApolloClient } from "@vue/apollo-composable";
import { ApolloClient, InMemoryCache } from "@apollo/client/core";
import type { ApolloQueryResult } from "@apollo/client/core";
import { getStore } from "@/store";
import type { User } from "@/models/user";
import type { Corollary } from "@/models/common";
import type { MessageApi } from "naive-ui";

const query = gql`
  query getUser {
    getUser {
      __typename
      ... on User {
        lastAppGroupName
        account {
          billingThreshold
          isDeactivated
          deactivatedAt
          packageId
          inTrial
          convertedAt
        }
      }
      ... on Corollary {
        code
        message
      }
    }
  }
`;

type getUserResponse = ApolloQueryResult<{
  getUser: User | Corollary;
}>;

/**
 * Load and set the user + account information for the user.
 * @param client -- apollo client
 * @param message
 */
async function setUserInformation(
  client: ApolloClient<InMemoryCache>,
  message: MessageApi
): Promise<void> {
  const store = getStore();

  const response = (await client.query({
    query,
    variables: {},
    fetchPolicy: "no-cache",
  })) as getUserResponse;

  const typeName = response.data.getUser.__typename;

  if (typeName != "User") {
    message.error(
      "Unable to access user information. Please reach out to support@telemetryhub.com for assistance."
    );

    return;
  }

  const user = response.data.getUser as User;

  await store.dispatch("user/verifyAndSetOnReload", { user: user });
}

/**
 * Retrieve apollo client and provide useSetUserInformation
 * function to get various user and account information.
 * @returns setUserInformation composition functionality.
 */
export function useSetUserInformation(): {
  setUserInformation: () => Promise<void>;
} {
  const { resolveClient } = useApolloClient();
  const message = useMessage();

  return {
    setUserInformation: () => setUserInformation(resolveClient(), message),
  };
}
